import type { components } from "@dolfin/api-admin";
import { formatCurrency } from "@/lib/format";

export type ApprovalPolicyResponse = components["schemas"]["ApprovalPolicyResponse"];
export type ApprovalPolicyAction = components["schemas"]["ApprovalPolicyAction"];
export type SupplierStanding = components["schemas"]["SupplierStanding"];

export const ACTION_OPTIONS: { value: ApprovalPolicyAction; label: string; description: string }[] = [
  { value: "AutoApprove", label: "Auto-approve", description: "Skip review and approve the bill immediately." },
  { value: "AlwaysReview", label: "Review", description: "Route the bill for approval." },
  { value: "AutoVoid", label: "Auto-void", description: "Automatically void the bill (terminal)." },
];

export const STANDING_OPTIONS: { value: SupplierStanding; label: string }[] = [
  { value: "Any", label: "Any supplier" },
  { value: "New", label: "New suppliers" },
  { value: "Established", label: "Established suppliers" },
];

export function actionLabel(action: ApprovalPolicyAction | undefined): string {
  return ACTION_OPTIONS.find((o) => o.value === action)?.label ?? action ?? "Unknown";
}

/** Matches the bill-page badge idiom (border/bg/text at 25/15/600). */
export function actionBadgeClass(action: ApprovalPolicyAction | undefined): string {
  switch (action) {
    case "AutoApprove":
      return "border-green-500/25 bg-green-500/15 text-green-600";
    case "AlwaysReview":
      return "border-amber-500/25 bg-amber-500/15 text-amber-600";
    case "AutoVoid":
      return "border-red-500/25 bg-red-500/15 text-red-600";
    default:
      return "";
  }
}

function standingSummary(standing: SupplierStanding | undefined): string | null {
  if (standing === "New") return "new suppliers";
  if (standing === "Established") return "established suppliers";
  return null;
}

/** e.g. "£500.00–£1,000.00 GBP · new suppliers" or "Any amount". Supplier scoping is shown separately. */
export function conditionSummary(policy: {
  amountGte?: number | string | null;
  amountLte?: number | string | null;
  currency?: string | null;
  supplierStanding?: SupplierStanding;
}): string {
  const parts: string[] = [];

  const hasGte = policy.amountGte != null && policy.amountGte !== "";
  const hasLte = policy.amountLte != null && policy.amountLte !== "";
  const ccy = policy.currency ?? "";
  if (hasGte && hasLte) {
    parts.push(`${formatCurrency(policy.amountGte!, ccy)}–${formatCurrency(policy.amountLte!, ccy)} ${ccy}`.trim());
  } else if (hasGte) {
    parts.push(`≥ ${formatCurrency(policy.amountGte!, ccy)} ${ccy}`.trim());
  } else if (hasLte) {
    parts.push(`≤ ${formatCurrency(policy.amountLte!, ccy)} ${ccy}`.trim());
  }

  const standing = standingSummary(policy.supplierStanding);
  if (standing) parts.push(standing);

  return parts.length ? parts.join(" · ") : "Any amount";
}
